import React, { useState, useMemo, useEffect } from "react";
import OverflowMenu from "@/shared/components/ui/OverflowMenu";
import { useTranslation } from "@/shared/contexts/TranslationContext";
import type {
  AnyEntry,
  FinancialEntry,
  VirtualEntry,
  RecurrenceRules,
  RecurrenceTemplate,
} from "@/modules/billing/entries/entriesTypes";
import {
  isOverdue,
  createEntry,
  partialMarkAsPaid,
  markAsPaid,
  updateEntry,
  replicateEntryBatch,
  undoPayment,
  deleteEntry,
} from "@/modules/billing/entries/entriesService";
import {
  createRecurrenceTemplate,
  updateRecurrenceTemplate,
  smartTerminateRecurrenceTemplate,
  maybeCleanupInstallmentTemplate,
  rollTemplateOrigin,
  handoverTemplateOrigin,
  getRecurrenceTemplate,
  countEntriesWithTemplate,
  findPreviousTerminatedTemplate,
  reactivateTemplate,
} from "@/modules/billing/entries/recurrencesService";
import { useUnit } from "@/modules/units/contexts/UnitContext";
import { useBilling } from "@/modules/billing/BillingContext";
import PayoffModal from "./PayoffModal";
import PostponeModal from "./PostponeModal";
import ReplicateModal from "./ReplicateModal";
import RecurrenceModal from "./RecurrenceModal";
import NotifyModal from "./NotifyModal";
import { ReceiptModal } from "./ReceiptModal";
import EntryModal from "./EntryModal";
import { ConfirmDialog, Modal } from "@/shared/components/ui";

interface Props {
  entry: AnyEntry;
  isMobile?: boolean;
  isMember?: boolean;
  onActionDone?: (action: string, entry: AnyEntry) => void;
  onEdit?: (entry: AnyEntry) => void;
  onPayoff?: (entry: AnyEntry) => void;
  onUndoPayment?: (entry: FinancialEntry) => void;
  onReplicate?: (entry: FinancialEntry) => void;
  onOpenReceipt?: (entry: FinancialEntry) => void;
  onRecurrenceConfig?: (entry: FinancialEntry) => void;
  onTerminateRecurrence?: (entry: FinancialEntry) => void;
  onPostpone?: (entry: AnyEntry) => void;
  onNotify?: (entry: FinancialEntry) => void;
  onDelete?: (entry: FinancialEntry) => void;
}

const EntryActions: React.FC<Props> = ({
  entry,
  isMobile = false,
  isMember = false,
  onActionDone,
  onEdit,
  onPayoff,
  onUndoPayment,
  onReplicate,
  onOpenReceipt,
  onRecurrenceConfig,
  onTerminateRecurrence,
  onPostpone,
  onNotify,
  onDelete,
}) => {
  const { t } = useTranslation();
  const te = (k: string, p?: Record<string, string>) =>
    t(`billing.entries.${k}`, p);

  // Members cannot perform any actions
  if (isMember) {
    return null;
  }

  const isVirtualEntry = (e: AnyEntry): e is VirtualEntry =>
    "isVirtual" in e && (e as any).isVirtual === true;
  const isPayable = (e: AnyEntry) => e.status === "open" || isOverdue(e);
  // Local modal state (ownership moved here to avoid duplication)
  const { invalidateEntries } = useBilling();
  const { currentUnit, userRole } = useUnit();

  const [payoffEntry, setPayoffEntry] = useState<AnyEntry | null>(null);
  const [payoffAmount, setPayoffAmount] = useState<string>("");
  const [payoffDate, setPayoffDate] = useState<string>(
    () => new Date().toISOString().split("T")[0],
  );
  const [payoffSubmitting, setPayoffSubmitting] = useState(false);

  const [postponeEntryState, setPostponeEntry] = useState<AnyEntry | null>(
    null,
  );
  const [postponeDate, setPostponeDate] = useState<string>("");
  const [postponeSubmitting, setPostponeSubmitting] = useState(false);

  const [replicateEntry, setReplicateEntry] = useState<FinancialEntry | null>(
    null,
  );
  const [replicateDate, setReplicateDate] = useState<string>(
    () => new Date().toISOString().split("T")[0],
  );
  const [replicateSubmitting, setReplicateSubmitting] = useState(false);
  const [repeatMode, setRepeatMode] = useState<"once">("once");

  const [recurrenceEntry, setRecurrenceEntry] = useState<FinancialEntry | null>(
    null,
  );
  const [recurrenceTemplateBeingEdited, setRecurrenceTemplateBeingEdited] =
    useState<string | null>(null);
  const [recurrencePeriodicity, setRecurrencePeriodicity] = useState<
    "daily" | "weekly" | "monthly" | "yearly" | "installment"
  >("monthly");
  const [recurrenceInstallmentTotal, setRecurrenceInstallmentTotal] =
    useState<number>(12);
  const [recurrenceInstallmentCurrent, setRecurrenceInstallmentCurrent] =
    useState<number>(1);
  const [recurrenceWeeklyDow, setRecurrenceWeeklyDow] = useState<number>(1);
  const [recurrenceDailyWeekdays, setRecurrenceDailyWeekdays] = useState<number[]>(
    [0, 1, 2, 3, 4, 5, 6],
  );
  const [recurrenceWeekendAdjust, setRecurrenceWeekendAdjust] = useState<
    "none" | "next" | "prev"
  >("none");
  const [recurrenceWeekdayLock, setRecurrenceWeekdayLock] =
    useState<boolean>(false);
  const [recurrenceWeekdayLockDay, setRecurrenceWeekdayLockDay] =
    useState<number>(1);
  const [recurrenceMonthlyTargetDay, setRecurrenceMonthlyTargetDay] = useState<
    number | null
  >(null);
  const [recurrenceMonthlyPreviewDates, setRecurrenceMonthlyPreviewDates] =
    useState<string[]>([]);
  const [recurrenceShowAdvanced, setRecurrenceShowAdvanced] =
    useState<boolean>(false);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<string | null>(
    null,
  );
  const [recurrenceSubmitting, setRecurrenceSubmitting] =
    useState<boolean>(false);

  const [notifyEntryState, setNotifyEntry] = useState<FinancialEntry | null>(
    null,
  );
  const [receiptEntry, setReceiptEntry] = useState<FinancialEntry | null>(null);
  const [editEntry, setEditEntry] = useState<AnyEntry | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<FinancialEntry | null>(
    null,
  );
  const [terminateEntry, setTerminateEntry] = useState<FinancialEntry | null>(
    null,
  );
  const [terminateSubmitting, setTerminateSubmitting] = useState(false);
  const [recurrenceScopeEntry, setRecurrenceScopeEntry] =
    useState<FinancialEntry | null>(null);
  const [recurrenceScopeSubmitting, setRecurrenceScopeSubmitting] =
    useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Load existing recurrence template for editing
  useEffect(() => {
    if (!recurrenceTemplateBeingEdited || !recurrenceEntry) return;

    const loadTemplate = async () => {
      try {
        const template = await getRecurrenceTemplate(
          recurrenceTemplateBeingEdited,
        );
        if (!template) return;

        // Set periodicity
        setRecurrencePeriodicity(template.periodicity as any);

        // Set rules from template
        const rules = (template.rules_json ?? {}) as any;

        if (template.periodicity === "weekly" && rules.weekly_dow) {
          setRecurrenceWeeklyDow(rules.weekly_dow);
        }

        if (
          template.periodicity === "monthly" ||
          template.periodicity === "installment"
        ) {
          if (rules.monthly_target_day) {
            setRecurrenceMonthlyTargetDay(rules.monthly_target_day);
          }
        }

        if (template.periodicity === "installment") {
          if (rules.installment_total) {
            setRecurrenceInstallmentTotal(rules.installment_total);
          }
          if (rules.installment_current_start) {
            setRecurrenceInstallmentCurrent(rules.installment_current_start);
          }
        }

        if (rules.weekend_adjust) {
          setRecurrenceWeekendAdjust(rules.weekend_adjust);
        }

        if (rules.weekday_lock) {
          setRecurrenceWeekdayLock(true);
          if (rules.weekday_lock_day) {
            setRecurrenceWeekdayLockDay(rules.weekday_lock_day);
          }
        }

        if (rules.daily_weekdays && rules.daily_weekdays.length > 0) {
          setRecurrenceDailyWeekdays(rules.daily_weekdays);
        }

        // Load end_date if the template was terminated
        setRecurrenceEndDate(template.end_date ?? null);
      } catch (err) {
        console.error("[EntryActions] Failed to load recurrence template:", err);
      }
    };

    loadTemplate();
  }, [recurrenceTemplateBeingEdited, recurrenceEntry]);

  const openFor = (action: string) => (e: AnyEntry) => {
    switch (action) {
      case "payoff":
        setPayoffDate(new Date().toISOString().split("T")[0]);
        setPayoffAmount(String(e.amount));
        setPayoffEntry(e);
        break;
      case "undo":
        handleUndoPayment(e as FinancialEntry);
        break;
      case "replicate":
        const nextDay = new Date((e as FinancialEntry).due_date);
        nextDay.setDate(nextDay.getDate() + 1);
        setReplicateDate(nextDay.toISOString().split("T")[0]);
        setRepeatMode("once");
        setReplicateEntry(e as FinancialEntry);
        break;
      case "receipt":
        setReceiptEntry(e as FinancialEntry);
        break;
      case "recurrence":
        // initialize recurrence defaults from entry
        const [ey, em, ed] = (e as FinancialEntry).due_date
          .split("-")
          .map(Number);
        const entryDow = new Date(ey, em - 1, ed).getDay();
        setRecurrenceEntry(e as FinancialEntry);
        setRecurrenceTemplateBeingEdited(null);
        setRecurrencePeriodicity("monthly");
        setRecurrenceInstallmentTotal(12);
        const match = (e as FinancialEntry).description.match(/(\d+)\/(\d+)$/);
        if (match) {
          setRecurrenceInstallmentCurrent(Number(match[1]));
          setRecurrenceInstallmentTotal(Number(match[2]));
        } else {
          setRecurrenceInstallmentCurrent(1);
        }
        setRecurrenceWeeklyDow(entryDow);
        setRecurrenceDailyWeekdays([0, 1, 2, 3, 4, 5, 6]);
        setRecurrenceWeekendAdjust("none");
        setRecurrenceWeekdayLock(false);
        setRecurrenceWeekdayLockDay(entryDow);
        setRecurrenceMonthlyTargetDay(
          Number((e as FinancialEntry).due_date.split("-")[2]),
        );
        setRecurrenceMonthlyPreviewDates([]);
        setRecurrenceShowAdvanced(false);
        setRecurrenceEndDate(null);
        break;
      case "editRecurrence":
        // Load existing recurrence template
        const editEntry = e as FinancialEntry;
        setRecurrenceEntry(editEntry);
        setRecurrenceTemplateBeingEdited(editEntry.recurrence_template_id || null);
        // Initialize defaults, will be overwritten by loadRecurrenceTemplate
        const [eyE, emE, edE] = editEntry.due_date
          .split("-")
          .map(Number);
        const entryDowE = new Date(eyE, emE - 1, edE).getDay();
        setRecurrencePeriodicity("monthly");
        setRecurrenceInstallmentTotal(12);
        setRecurrenceInstallmentCurrent(1);
        setRecurrenceWeeklyDow(entryDowE);
        setRecurrenceDailyWeekdays([0, 1, 2, 3, 4, 5, 6]);
        setRecurrenceWeekendAdjust("none");
        setRecurrenceWeekdayLock(false);
        setRecurrenceWeekdayLockDay(entryDowE);
        setRecurrenceMonthlyTargetDay(
          Number(editEntry.due_date.split("-")[2]),
        );
        setRecurrenceMonthlyPreviewDates([]);
        setRecurrenceShowAdvanced(false);
        // endDate will be populated by useEffect after loading template
        setRecurrenceEndDate(null);
        break;
      case "terminateRecurrence":
        setTerminateEntry(e as FinancialEntry);
        break;
      case "postpone":
        setPostponeDate((e as AnyEntry).due_date);
        setPostponeEntry(e);
        break;
      case "notify":
        setNotifyEntry(e as FinancialEntry);
        break;
      case "edit":
        setEditEntry(e);
        break;
      case "delete":
        setConfirmDelete(e as FinancialEntry);
        break;
    }
  };

  const items: any[] = [];
  if (isVirtualEntry(entry)) {
    // always show payoff for virtual entries
    items.push({
      label: te("actions.payoff"),
      icon: (
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      onClick: () => {
        if (onPayoff) onPayoff(entry);
        else openFor("payoff")(entry);
      },
    });

    // Recurrence actions for virtual entries
    items.push({
      label: te("actions.editRecurrence") || te("actions.configureRecurrence"),
      icon: (
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      ),
      onClick: () => {
        if (onRecurrenceConfig) onRecurrenceConfig(entry as FinancialEntry);
        else openFor("editRecurrence")(entry);
      },
    });

    // Terminate recurrence for virtual entries
    items.push({
      label: te("actions.terminateRecurrence"),
      icon: (
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      ),
      onClick: () => {
        if (onTerminateRecurrence)
          onTerminateRecurrence(entry as FinancialEntry);
        else openFor("terminateRecurrence")(entry);
      },
    });
  } else {
    if (isPayable(entry)) {
      items.push({
        label: te("actions.payoff"),
        icon: (
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ),
        onClick: () => {
          if (onPayoff) onPayoff(entry);
          else openFor("payoff")(entry);
        },
      });
    }

    // paid-specific actions handled below (show cancel payment instead of edit)

    items.push({
      label: te("actions.repeat"),
      icon: (
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      ),
      onClick: () => {
        if (onReplicate) onReplicate(entry as FinancialEntry);
        else openFor("replicate")(entry);
      },
    });

    if (
      !(entry as any).isVirtual &&
      (entry as FinancialEntry).status === "paid"
    ) {
      items.push({
        label: (entry as FinancialEntry).receipt_url
          ? te("actions.viewReceipt")
          : te("actions.attachReceipt"),
        icon: (entry as FinancialEntry).receipt_url ? (
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
            />
          </svg>
        ) : (
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
            />
          </svg>
        ),
        onClick: () => {
          if (onOpenReceipt) onOpenReceipt(entry as FinancialEntry);
          else openFor("receipt")(entry);
        },
      });
    }

    // Recurrence
    items.push({
      label: (entry as FinancialEntry).recurrence_template_id
        ? te("actions.editRecurrence") || te("actions.terminateRecurrence")
        : te("actions.configureRecurrence"),
      icon: (
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      ),
      onClick: () => {
        if ((entry as FinancialEntry).recurrence_template_id) {
          if (onRecurrenceConfig) onRecurrenceConfig(entry as FinancialEntry);
          else openFor("editRecurrence")(entry);
        } else {
          if (onRecurrenceConfig) onRecurrenceConfig(entry as FinancialEntry);
          else openFor("recurrence")(entry);
        }
      },
    });

    // Terminate recurrence (only if already has recurrence)
    if ((entry as FinancialEntry).recurrence_template_id) {
      items.push({
        label: te("actions.terminateRecurrence"),
        icon: (
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        ),
        onClick: () => {
          if (onTerminateRecurrence)
            onTerminateRecurrence(entry as FinancialEntry);
          else openFor("terminateRecurrence")(entry);
        },
      });
    }

    if ((entry as FinancialEntry).status === "open") {
      items.push({
        label: te("actions.postpone"),
        icon: (
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3M3 11h18M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        ),
        onClick: () => {
          if (onPostpone) onPostpone(entry);
          else openFor("postpone")(entry);
        },
      });
    }

    items.push({
      label: te("actions.notify"),
      icon: (
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
      ),
      onClick: () => {
        if (onNotify) onNotify(entry as FinancialEntry);
        else openFor("notify")(entry);
      },
    });
  }

  // Edit vs Cancel Payment: show Edit for non-paid entries, Cancel Payment for paid ones
  if ((entry as FinancialEntry).status === "paid") {
    items.unshift({
      label: te("actions.cancelPayment"),
      icon: (
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
          />
        </svg>
      ),
      onClick: () => openFor("undo")(entry),
    });
  } else {
    items.unshift({
      label: te("actions.edit"),
      icon: (
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
      ),
      onClick: () => {
        if (onEdit) onEdit(entry);
        else openFor("edit")(entry);
      },
    });
  }

  // Delete (show when deletable) - prefer parent's handler if provided
  if (
    !("isVirtual" in entry && (entry as any).isVirtual) &&
    (entry as FinancialEntry).status !== "paid"
  ) {
    items.push({
      label: te("actions.delete"),
      icon: (
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
          />
        </svg>
      ),
      onClick: () => {
        if (onDelete) onDelete(entry as FinancialEntry);
        else openFor("delete")(entry);
      },
      danger: true,
    });
  }

  // Os itens jÃ¡ nascem com o onClick correto.
  // NÃ£o remapear por label traduzido, porque isso mistura "configurar recorrÃªncia"
  // com "encerrar recorrÃªncia" e pode abrir o diÃ¡logo errado.
  const finalItems = items;

  // Helpers & action handlers
  const materialiseVirtualEntry = async (virtual: VirtualEntry) => {
    const entry = await createEntry({
      unit_id: virtual.unit_id,
      entry_type: virtual.entry_type,
      description: virtual.description,
      amount: virtual.amount,
      due_date: virtual.due_date,
      // Preserve occurrence date so SQL dedup works even if due_date is later edited
      original_due_date: virtual.due_date,
      party_id: virtual.party_id ?? undefined,
      party_name_snapshot: virtual.party_name_snapshot ?? undefined,
      party_whatsapp_snapshot: virtual.party_whatsapp_snapshot ?? undefined,
      party_pix_key_snapshot: virtual.party_pix_key_snapshot ?? undefined,
      payment_method_snapshot: virtual.payment_method_snapshot ?? undefined,
      recurrence_template_id: virtual.recurrence_template_id,
      notify_on_due: (virtual as any).notify_on_due ?? false,
      notify_time: (virtual as any).notify_time ?? undefined,
      notify_offset_minutes:
        (virtual as any).notify_offset_minutes ?? undefined,
    });
    // virtual._template is constructed with hardcoded periodicity â€” fetch the real one
    try {
      const realTemplate = await getRecurrenceTemplate(virtual.recurrence_template_id);
      if (realTemplate) {
        // Cleanup must run before rolling so it uses the pre-roll origin for counting
        await maybeCleanupInstallmentTemplate(realTemplate);
        // Roll origin: this template's "protagonist" is now this entry
        await rollTemplateOrigin(realTemplate, entry.id);
      }
    } catch (err) {
      console.warn("[materialiseVirtualEntry] post-materialise cleanup warning", err);
    }
    return entry;
  };

  const notifyDone = (action: string, affectedEntry: AnyEntry) => {
    setReloadKey((k) => k + 1);
    invalidateEntries();
    onActionDone?.(action, affectedEntry);
  };

  const handleUndoPayment = async (target: FinancialEntry) => {
    try {
      await undoPayment(target.id, userRole || undefined);
      notifyDone("undoPayment", target);
    } catch (err) {
      console.error("[EntryActions] undo payment error:", err);
    }
  };

  const addDays = (dateStr: string, days: number): string => {
    const [y, m, d] = dateStr.split("-").map(Number);
    const date = new Date(y, m - 1, d + days);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  };

  const buildFutureTemplateRules = (
    template: RecurrenceTemplate,
    baseEntry: FinancialEntry,
  ): RecurrenceRules => {
    const rules: RecurrenceRules = {
      ...((template.rules_json ?? {}) as RecurrenceRules),
    };

    if (
      template.periodicity === "monthly" ||
      template.periodicity === "installment"
    ) {
      (rules as any).monthly_target_day = Number(
        baseEntry.due_date.split("-")[2],
      );
    }

    if (template.periodicity === "installment") {
      const match = String(baseEntry.description || "").match(/(\d+)\/(\d+)$/);
      if (match) {
        (rules as any).installment_current_start = Number(match[1]);
        (rules as any).installment_total = Number(match[2]);
      }
    }

    return rules;
  };

  const handleRecurrenceScopeThisOnly = async () => {
    if (!recurrenceScopeEntry) return;
    const affected = recurrenceScopeEntry;
    setRecurrenceScopeEntry(null);
    notifyDone("editThisOccurrence", affected);
  };

  const handleRecurrenceScopeFuture = async () => {
    if (
      !recurrenceScopeEntry ||
      !currentUnit?.id ||
      !recurrenceScopeEntry.recurrence_template_id
    )
      return;
    const baseEntry = recurrenceScopeEntry;
    const templateId = baseEntry.recurrence_template_id as string;
    setRecurrenceScopeSubmitting(true);

    try {
      const oldTemplate = await getRecurrenceTemplate(templateId);
      if (!oldTemplate)
        throw new Error("Template de recorrÃªncia nÃ£o encontrado");

      const dayBeforeEdit = addDays(baseEntry.due_date, -1);

      // MantÃ©m a recorrÃªncia antiga vÃ¡lida somente atÃ© a ocorrÃªncia anterior.
      // Isso preserva o histÃ³rico e permite reativaÃ§Ã£o caso a quebra seja desfeita.
      await updateRecurrenceTemplate(oldTemplate.id, {
        end_date: dayBeforeEdit,
      } as any);

      const newTemplate = await createRecurrenceTemplate({
        unit_id: currentUnit.id,
        origin_entry_id: baseEntry.id,
        periodicity: oldTemplate.periodicity as any,
        rules_json: buildFutureTemplateRules(oldTemplate, baseEntry),
        start_date: baseEntry.due_date,
      });

      await updateEntry(baseEntry.id, {
        recurrence_template_id: newTemplate.id,
      }, userRole || undefined);

      setRecurrenceScopeEntry(null);
      notifyDone("editThisAndFutureOccurrences", {
        ...baseEntry,
        recurrence_template_id: newTemplate.id,
      } as FinancialEntry);
    } catch (err) {
      console.error("[EntryActions] recurrence scope future error:", err);
    } finally {
      setRecurrenceScopeSubmitting(false);
    }
  };

  const handleConfirmPayoff = async () => {
    if (!payoffEntry) return;
    const paid = Number(payoffAmount);
    if (!payoffDate || paid <= 0 || paid > payoffEntry.amount) return;
    setPayoffSubmitting(true);
    try {
      let realEntry: any = payoffEntry;
      if (isVirtualEntry(payoffEntry)) {
        realEntry = await materialiseVirtualEntry(payoffEntry);
      }
      if (paid < realEntry.amount) {
        await partialMarkAsPaid(realEntry, paid, payoffDate, userRole || undefined);
      } else {
        await markAsPaid(realEntry.id, payoffDate, userRole || undefined);
      }
      setPayoffEntry(null);
      setReloadKey((k) => k + 1);
      invalidateEntries();
      onActionDone?.("payoff", realEntry);
    } catch (err) {
      console.error("[EntryActions] handleConfirmPayoff error:", err);
    } finally {
      setPayoffSubmitting(false);
    }
  };

  const handleConfirmPostpone = async () => {
    if (!postponeEntryState) return;
    if (!postponeDate) return;
    setPostponeSubmitting(true);
    try {
      let realEntry: any = postponeEntryState;
      if (isVirtualEntry(postponeEntryState)) {
        realEntry = await materialiseVirtualEntry(postponeEntryState);
      }
      const original = realEntry.original_due_date ?? realEntry.due_date;
      await updateEntry(realEntry.id, {
        due_date: postponeDate,
        original_due_date: original,
      }, userRole || undefined);
      setPostponeEntry(null);
      setReloadKey((k) => k + 1);
      invalidateEntries();
      onActionDone?.("postpone", realEntry);
    } catch (err) {
      console.error("[EntryActions] postpone error:", err);
    } finally {
      setPostponeSubmitting(false);
    }
  };

  const replicateOccurrences = useMemo(() => {
    if (!replicateEntry || !replicateDate) return [];
    return [
      { due_date: replicateDate, description: replicateEntry.description },
    ];
  }, [replicateEntry, replicateDate]);

  const handleConfirmReplicate = async () => {
    if (!replicateEntry || replicateOccurrences.length === 0) return;
    setReplicateSubmitting(true);
    try {
      await replicateEntryBatch(replicateEntry, replicateOccurrences);
      setReplicateEntry(null);
      setReloadKey((k) => k + 1);
      invalidateEntries();
      onActionDone?.("replicate", replicateEntry as AnyEntry);
    } catch (err) {
      console.error("[EntryActions] replicate error:", err);
    } finally {
      setReplicateSubmitting(false);
    }
  };

  const handleConfirmRecurrenceConfig = async () => {
    if (!recurrenceEntry || !currentUnit?.id) return;
    setRecurrenceSubmitting(true);
    try {
      const rules: RecurrenceRules = {};
      if (recurrencePeriodicity === "installment") {
        (rules as any).installment_total = recurrenceInstallmentTotal;
        (rules as any).installment_current_start = recurrenceInstallmentCurrent;
      }
      if (recurrencePeriodicity === "weekly")
        (rules as any).weekly_dow = recurrenceWeeklyDow;
      if (recurrencePeriodicity === "daily")
        (rules as any).daily_weekdays = recurrenceDailyWeekdays;
      if (
        recurrencePeriodicity === "monthly" ||
        recurrencePeriodicity === "installment"
      ) {
        if (recurrenceMonthlyTargetDay)
          (rules as any).monthly_target_day = recurrenceMonthlyTargetDay;
      }
      if (recurrenceWeekendAdjust !== "none")
        (rules as any).weekend_adjust = recurrenceWeekendAdjust;
      if (recurrenceWeekdayLock) {
        (rules as any).weekday_lock = true;
        (rules as any).weekday_lock_day = recurrenceWeekdayLockDay;
      }

      // Check if we're editing an existing template or creating a new one
      if (recurrenceTemplateBeingEdited) {
        // Update existing template; if end_date cleared â†’ reactivate, otherwise keep/set
        await updateRecurrenceTemplate(recurrenceTemplateBeingEdited, {
          periodicity: recurrencePeriodicity,
          rules_json: rules,
          end_date: recurrenceEndDate ?? null,
          status: recurrenceEndDate ? 'terminated' : 'active',
        } as any);
      } else {
        // Create new template
        const template = await createRecurrenceTemplate({
          unit_id: currentUnit.id,
          origin_entry_id: recurrenceEntry.id,
          periodicity: recurrencePeriodicity,
          rules_json: rules,
          start_date: recurrenceEntry.due_date,
        });

        let updateData: any = { recurrence_template_id: template.id };
        if (recurrencePeriodicity === "installment") {
          const baseDesc = recurrenceEntry.description.replace(
            /\s+\d+\/\d+$/,
            "",
          );
          updateData.description = `${baseDesc} ${recurrenceInstallmentCurrent}/${recurrenceInstallmentTotal}`;
        }
        await updateEntry(recurrenceEntry.id, updateData, userRole || undefined);
      }

      setRecurrenceEntry(null);
      setRecurrenceTemplateBeingEdited(null);
      setRecurrenceEndDate(null);
      setReloadKey((k) => k + 1);
      invalidateEntries();
      onActionDone?.("recurrenceConfig", recurrenceEntry as AnyEntry);
    } catch (err) {
      console.error("[EntryActions] handleConfirmRecurrenceConfig error:", err);
      alert("Erro ao configurar recorrÃªncia");
    } finally {
      setRecurrenceSubmitting(false);
    }
  };

  const handleTerminateRecurrence = async () => {
    if (!terminateEntry?.recurrence_template_id) return;
    const target = terminateEntry;
    setTerminateSubmitting(true);
    try {
      // Calculate end_date as the day before the entry's due_date
      // (so terminating on 15/05 â†’ end_date = 14/05, virtuals still show up to 14/05)
      const [y, m, d] = target.due_date.split('-').map(Number);
      const dayBefore = new Date(y, m - 1, d - 1);
      const endDate = `${dayBefore.getFullYear()}-${String(dayBefore.getMonth() + 1).padStart(2, '0')}-${String(dayBefore.getDate()).padStart(2, '0')}`;

      if (isVirtualEntry(target)) {
        // Virtual entries don't need to be materialized just to terminate the recurrence.
        // Use the template's origin_entry_id as the reference entry.
        await smartTerminateRecurrenceTemplate(
          target.recurrence_template_id as string,
          (target as VirtualEntry)._template.origin_entry_id,
          endDate,
          userRole || undefined,
        );
      } else {
        await smartTerminateRecurrenceTemplate(
          target.recurrence_template_id as string,
          target.id,
          endDate,
          userRole || undefined,
        );
        await updateEntry(target.id, { recurrence_template_id: null }, userRole || undefined);
      }

      setTerminateEntry(null);
      notifyDone("terminateRecurrence", target);
    } catch (err) {
      console.error("[EntryActions] terminate error:", err);
    } finally {
      setTerminateSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    const target = confirmDelete;

    try {
      let previousTemplateToReactivate: RecurrenceTemplate | null = null;

      // Se esta conta for a base de uma quebra de recorrÃªncia, apagar ela pode
      // significar que o usuÃ¡rio quer desfazer a quebra. Antes de excluir,
      // procuramos um template anterior encerrado para restaurar depois.
      if (target.recurrence_template_id && currentUnit?.id) {
        try {
          const template = await getRecurrenceTemplate(
            target.recurrence_template_id,
          );
          if (template) {
            // If deleting the current origin, pass the baton to the next most
            // recent linked entry so the template is not cascade-deleted
            if (target.id === template.origin_entry_id) {
              await handoverTemplateOrigin(template.id, target.id);
            }
            const otherCount = await countEntriesWithTemplate(
              template.id,
              template.origin_entry_id,
            );
            if (otherCount === 0) {
              previousTemplateToReactivate =
                await findPreviousTerminatedTemplate(
                  currentUnit.id,
                  template.start_date,
                );
            }
          }
        } catch (err) {
          console.warn(
            "[EntryActions] template recovery pre-fetch warning:",
            err,
          );
        }
      }

      await deleteEntry(target.id, userRole || undefined);

      if (previousTemplateToReactivate) {
        try {
          await reactivateTemplate(previousTemplateToReactivate.id);
        } catch (err) {
          console.warn("[EntryActions] template recovery warning:", err);
        }
      }

      setConfirmDelete(null);
      notifyDone("delete", target);
    } catch (err) {
      console.error("[EntryActions] delete error:", err);
    }
  };

  const handleReceiptUpdated = (updated: FinancialEntry) => {
    setReceiptEntry(null);
    setReloadKey((k) => k + 1);
    invalidateEntries();
    onActionDone?.("receiptUpdated", updated);
  };

  return (
    <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
      <OverflowMenu items={finalItems} />

      <PayoffModal
        entry={payoffEntry}
        isOpen={!!payoffEntry}
        amount={payoffAmount}
        date={payoffDate}
        submitting={payoffSubmitting}
        setAmount={setPayoffAmount}
        setDate={setPayoffDate}
        onClose={() => setPayoffEntry(null)}
        onConfirm={handleConfirmPayoff}
        isMobile={isMobile}
      />

      <PostponeModal
        entry={postponeEntryState}
        isOpen={!!postponeEntryState}
        date={postponeDate}
        submitting={postponeSubmitting}
        setDate={setPostponeDate}
        onClose={() => setPostponeEntry(null)}
        onConfirm={handleConfirmPostpone}
        isMobile={isMobile}
      />

      <ReplicateModal
        sourceEntry={replicateEntry}
        isOpen={!!replicateEntry}
        replicateDate={replicateDate}
        setReplicateDate={setReplicateDate}
        replicateOccurrences={replicateOccurrences}
        replicateSubmitting={replicateSubmitting}
        onClose={() => setReplicateEntry(null)}
        onConfirm={handleConfirmReplicate}
        repeatMode={repeatMode}
        setRepeatMode={setRepeatMode}
        isMobile={isMobile}
      />

      <RecurrenceModal
        entry={recurrenceEntry}
        isOpen={!!recurrenceEntry}
        periodicity={recurrencePeriodicity}
        setPeriodicity={setRecurrencePeriodicity}
        monthlyTargetDay={recurrenceMonthlyTargetDay}
        setMonthlyTargetDay={setRecurrenceMonthlyTargetDay}
        monthlyPreviewDates={recurrenceMonthlyPreviewDates}
        weeklyDow={recurrenceWeeklyDow}
        setWeeklyDow={setRecurrenceWeeklyDow}
        installmentCurrent={recurrenceInstallmentCurrent}
        setInstallmentCurrent={setRecurrenceInstallmentCurrent}
        installmentTotal={recurrenceInstallmentTotal}
        setInstallmentTotal={setRecurrenceInstallmentTotal}
        showAdvanced={recurrenceShowAdvanced}
        setShowAdvanced={setRecurrenceShowAdvanced}
        weekendAdjust={recurrenceWeekendAdjust}
        setWeekendAdjust={setRecurrenceWeekendAdjust}
        weekdayLock={recurrenceWeekdayLock}
        setWeekdayLock={setRecurrenceWeekdayLock}
        weekdayLockDay={recurrenceWeekdayLockDay}
        setWeekdayLockDay={setRecurrenceWeekdayLockDay}
        dailyWeekdays={recurrenceDailyWeekdays}
        setDailyWeekdays={setRecurrenceDailyWeekdays}
        endDate={recurrenceEndDate}
        setEndDate={setRecurrenceEndDate}
        submitting={recurrenceSubmitting}
        onClose={() => setRecurrenceEntry(null)}
        onConfirm={handleConfirmRecurrenceConfig}
        isMobile={isMobile}
      />

      <NotifyModal
        entry={notifyEntryState}
        onClose={() => setNotifyEntry(null)}
        onSaved={() => {
          // Use centralized notifier so parent/list components receive the action
          notifyDone("notify", (notifyEntryState as AnyEntry) ?? (entry as AnyEntry));
          setNotifyEntry(null);
        }}
        isMobile={isMobile}
      />

      {receiptEntry && (
        <ReceiptModal
          entry={receiptEntry}
          onClose={() => setReceiptEntry(null)}
          onUpdated={handleReceiptUpdated}
        />
      )}

      <EntryModal
        key={editEntry?.id ?? "entry-modal"}
        isOpen={!!editEntry}
        initialEntry={editEntry ?? undefined}
        entryType={(editEntry?.entry_type ?? "receivable") as any}
        onClose={() => setEditEntry(null)}
        onSaved={(saved: FinancialEntry) => {
          setEditEntry(null);

          const hadRecurrence = !!(
            saved.recurrence_template_id ||
            (editEntry as FinancialEntry | null)?.recurrence_template_id ||
            (editEntry as VirtualEntry | null)?.isVirtual
          );

          if (hadRecurrence && saved.recurrence_template_id) {
            // A mesma pergunta usada para conta fÃ­sica recorrente tambÃ©m atende a virtual.
            // Para virtual, o EntryModal materializa primeiro em uma conta fÃ­sica
            // vinculada ao template original e devolve essa conta real em onSaved(saved).
            setRecurrenceScopeEntry(saved);
            return;
          }

          notifyDone("edit", saved);
        }}
      />

      {recurrenceScopeEntry && (
        <Modal
          isOpen={!!recurrenceScopeEntry}
          onClose={() =>
            !recurrenceScopeSubmitting && setRecurrenceScopeEntry(null)
          }
          title={te("recurrence.edit.title") || "Alterar RecorrÃªncia"}
          size="sm"
        >
          <div className="text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/20">
              <svg
                className="h-6 w-6 text-yellow-600 dark:text-yellow-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {te("recurrence.edit.message") ||
                  "Deseja alterar apenas essa ocorrÃªncia ou essa e as prÃ³ximas?"}
              </p>
            </div>
          </div>
          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button
              type="button"
              onClick={handleRecurrenceScopeThisOnly}
              disabled={recurrenceScopeSubmitting}
              className="inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {te("recurrence.edit.this") || "Apenas essa"}
            </button>
            <button
              type="button"
              onClick={handleRecurrenceScopeFuture}
              disabled={recurrenceScopeSubmitting}
              className="inline-flex justify-center rounded-md border border-transparent bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors px-4 py-2 text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {te("recurrence.edit.future") || "Essa e as prÃ³ximas"}
            </button>
          </div>
        </Modal>
      )}

      <ConfirmDialog
        title={te("record.delete")}
        message={te("record.deleteConfirm")}
        confirmText={te("actions.delete")}
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleConfirmDelete}
        type="danger"
      />

      <ConfirmDialog
        title={te("recurrence.terminate.title") || "Encerrar recorrÃªncia"}
        message={
          te("recurrence.terminate.message") ||
          "Deseja encerrar a recorrÃªncia a partir desta conta?"
        }
        confirmText={
          te("recurrence.terminate.confirm") || "Encerrar recorrÃªncia"
        }
        isOpen={!!terminateEntry}
        onClose={() => !terminateSubmitting && setTerminateEntry(null)}
        onConfirm={handleTerminateRecurrence}
        type="warning"
      />
    </div>
  );
};

export default EntryActions;
